import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useUser } from "./UserContext";

// 파트너 원장(partner_program) 한 행을 레이아웃(좌측 레일)과 허브 화면이 나눠 쓴다 (2026-09-10 N3).
//   · 레이아웃이 열릴 때 본인 행을 한 번 읽는다 (RLS self_select — 읽기만, 상태 계산은 하지 않는다)
//   · PartnerHub 가 partner_program_sync() 로 받은 최신 행을 setProgram 으로 밀어 넣는다 — 서명·수료 뒤 레일 배지가 바로 바뀐다
//   · 행이 아직 없으면(첫 방문) 허브의 RPC 가 만들고 밀어 넣을 때까지 null
// 🔒 여기서 status 를 계산하거나 쓰지 않는다 — 판정은 RPC 만 (docs/pages/main/PartnerHub.md 「🔒 중요 코드」).

const PartnerProgramContext = createContext(null);

// (2026-09-23) terms_versions_required · mark_blocked_at 추가 — 자료실(PartnerLibrary)이 이 컨텍스트의 program 으로 부속 C 요구 버전을 대조하는데
//   열이 없어 termsStale 이 항상 참 → 「마크 사용 규정 문안이 갱신 배포 중」으로 내려받기가 잠겼다(대표 실기기 QA 2026-09-23 00:15).
const RAIL_COLUMNS = "partner_email, partner_type, type_confirmed, declared_type, status, partner_code, lesson_deadline, pending_rate, rate_effective_from, terms_versions_required, mark_blocked_at";

/**
 * previewProgram — Partner 역할이 없는 어드민 미리보기(2026-09-11): 원장을 읽지 않고 이 가짜 행(partnerNav.PREVIEW_PROGRAM)을 그대로 쓴다.
 *   레일·띠·정산 화면이 「새 파트너의 첫 화면(서명 전)」을 그리게 된다. 🔒 DB 에 쓰지 않는다 — RPC 도 부르지 않는다.
 */
export function PartnerProgramProvider({ children, previewProgram = null }) {
  const { userInfo } = useUser();
  const email = userInfo?.email;
  const [program, setProgram] = useState(previewProgram);

  useEffect(() => {
    if (!email || previewProgram) return undefined;
    let alive = true;
    supabase
      .from("partner_program")
      .select(RAIL_COLUMNS)
      .eq("partner_email", email)
      .maybeSingle()
      .then(({ data }) => {
        // 허브가 먼저 최신 행을 밀어 넣었으면 그 값을 지키고, 없을 때만 채운다
        if (alive && data) setProgram((prev) => prev || data);
      })
      .catch(() => { /* 읽기 실패는 레일이 배지 없이 그려지는 것으로 충분하다 — 허브가 오류 카드를 맡는다 */ });
    return () => { alive = false; };
  }, [email, previewProgram]);

  const value = useMemo(() => ({ program, setProgram }), [program]);
  return <PartnerProgramContext.Provider value={value}>{children}</PartnerProgramContext.Provider>;
}

/** Provider 밖(테스트·어드민 미리보기)에서는 null — 호출부는 `ctx?.` 로 쓴다 */
export function usePartnerProgram() {
  return useContext(PartnerProgramContext);
}
